import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  useCustomDisplayFormats,
  useCreateCustomDisplayFormat,
  useUpdateCustomDisplayFormat,
  useDeleteCustomDisplayFormat,
} from '../hooks/useCustomDisplayFormats';
import type { CustomDisplayFormat } from '../services/customDisplayFormat.service';
import {
  isValidD3Format,
  formatWithSpec,
  getReservedFormatValues,
} from '../services/formatting.service';
import { ApiError } from '../services/api.service';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a NEW format is created so the caller can select it. */
  onCreated?: (format: CustomDisplayFormat) => void;
}

// Representative values to preview how a spec renders.
const SAMPLE_VALUES = [1234.567, 0.1234, -9876543, 0];

/** Pull a human message out of a Flask `abort(code, description=...)` response. */
function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.payload?.msg) {
    // Backend msg looks like "409 Conflict: <description>" — drop the prefix.
    return err.payload.msg.replace(/^\d+\s+[\w\s]+:\s*/, '');
  }
  return fallback;
}

export function CustomFormatDialog({ open, onOpenChange, onCreated }: Props) {
  const { data: formats = [] } = useCustomDisplayFormats();
  const createMutation = useCreateCustomDisplayFormat();
  const updateMutation = useUpdateCustomDisplayFormat();
  const deleteMutation = useDeleteCustomDisplayFormat();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [spec, setSpec] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const reserved = useMemo(() => getReservedFormatValues(), []);
  const specValid = isValidD3Format(spec.trim());

  const validationError = useMemo(() => {
    const trimmedLabel = label.trim();
    const trimmedSpec = spec.trim();
    if (!trimmedLabel || !trimmedSpec) {
      return null; // don't nag before there's something to validate
    }
    if (!specValid) {
      return `"${trimmedSpec}" is not a valid d3-format string.`;
    }
    if (reserved.labels.has(trimmedLabel.toLowerCase())) {
      return `"${trimmedLabel}" is a built-in format name — pick another label.`;
    }
    if (reserved.specs.has(trimmedSpec)) {
      return `"${trimmedSpec}" is already provided as a built-in format.`;
    }
    const dupLabel = formats.find(
      (f) => f.id !== editingId && f.label.trim().toLowerCase() === trimmedLabel.toLowerCase(),
    );
    if (dupLabel) {
      return `A custom format labeled "${trimmedLabel}" already exists.`;
    }
    const dupSpec = formats.find((f) => f.id !== editingId && f.d3_format.trim() === trimmedSpec);
    if (dupSpec) {
      return `A custom format with the spec "${trimmedSpec}" already exists.`;
    }
    return null;
  }, [label, spec, specValid, reserved, formats, editingId]);

  const canSave = !!label.trim() && !!spec.trim() && specValid && !validationError;

  const resetForm = () => {
    setEditingId(null);
    setLabel('');
    setSpec('');
  };

  const handleSave = async () => {
    if (!canSave) return;
    const data = { label: label.trim(), d3_format: spec.trim() };
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data });
        toast.success('Format updated');
        resetForm();
      } else {
        const saved = await createMutation.mutateAsync(data);
        toast.success('Format created');
        resetForm();
        onCreated?.(saved);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save format'));
    }
  };

  const handleEdit = (f: CustomDisplayFormat) => {
    setEditingId(f.id);
    setLabel(f.label);
    setSpec(f.d3_format);
    setConfirmDeleteId(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Format deleted');
      if (editingId === id) resetForm();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to delete format'));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Custom display formats</DialogTitle>
          <DialogDescription>
            Define a reusable format from a{' '}
            <a
              href="https://d3js.org/d3-format#locale_format"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              d3-format
            </a>{' '}
            string. It becomes available wherever display formats are chosen.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {/* Add / edit form */}
          <div className="space-y-3 rounded-md border p-4">
            <div className="space-y-2">
              <Label htmlFor="cf-label">Label</Label>
              <Input
                id="cf-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Basis points"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf-spec">Format</Label>
              <Input
                id="cf-spec"
                value={spec}
                onChange={(e) => setSpec(e.target.value)}
                placeholder="e.g. $,.2f"
                className="font-mono"
              />
            </div>

            {/* Live preview */}
            {spec.trim() && specValid && (
              <div className="space-y-1 rounded bg-muted/50 p-2 text-sm">
                <div className="text-xs font-medium text-muted-foreground">Preview</div>
                {SAMPLE_VALUES.map((v) => (
                  <div key={v} className="flex justify-between font-mono">
                    <span className="text-muted-foreground">{v}</span>
                    <span>{formatWithSpec(v, spec.trim())}</span>
                  </div>
                ))}
              </div>
            )}

            {validationError && <p className="text-sm text-destructive">{validationError}</p>}

            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSave} disabled={!canSave || saving}>
                {editingId ? 'Update format' : (
                  <>
                    <Plus className="mr-1 h-4 w-4" /> Add format
                  </>
                )}
              </Button>
              {editingId && (
                <Button size="sm" variant="ghost" onClick={resetForm} disabled={saving}>
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {/* Existing formats */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Your custom formats</div>
            {formats.length === 0 ? (
              <p className="text-sm text-muted-foreground">No custom formats yet.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {formats.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{f.label}</div>
                      <div className="truncate font-mono text-xs text-muted-foreground">
                        {f.d3_format}
                      </div>
                    </div>
                    {confirmDeleteId === f.id ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="text-xs text-muted-foreground">Delete?</span>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(f.id)}
                          disabled={deleteMutation.isPending}
                        >
                          Yes
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => handleEdit(f)}
                          aria-label={`Edit ${f.label}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setConfirmDeleteId(f.id)}
                          aria-label={`Delete ${f.label}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
